"use client";

import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useRouter } from "next/navigation";
import { textColor, textSize } from "@/lib/style";
import { getNotifications, markNotificationAsRead, markNotificationAsUnread } from "./action";
import { Notification } from "@/types/notification";

const NotificationMenu = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);
    const [activeTab, setActiveTab] = useState<'unread' | 'all'>('unread');
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const lastFetchRef = useRef<string>(''); // Store last fetch result for comparison

    const router = useRouter();

    // Memoize notification handlers to prevent recreation
    const handleReadNotification = useCallback(async (guid: string) => {
        const notification = notifications?.find((notification) => notification.guid === guid);
        if (!notification) return;

        if (!notification.read) {
            await markNotificationAsRead(guid);
            setNotifications(prev => {
                const updatedNotifications = prev.map(n =>
                    n.guid === guid ? { ...n, read: true } : n
                );
                // Only update if there's an actual change
                return JSON.stringify(prev) !== JSON.stringify(updatedNotifications)
                    ? updatedNotifications
                    : prev;
            });
        }

        setIsMenuOpen(false);
        router.push(notification.followUpUri || '/');
    }, [notifications, router]);

    const handleMarkUnread = useCallback(async (e: React.MouseEvent, guid: string) => {
        e.stopPropagation();
        await markNotificationAsUnread(guid);

        setNotifications(prev => {
            const updatedNotifications = prev.map(n =>
                n.guid === guid ? { ...n, read: false } : n
            );
            // Only update if there's an actual change
            return JSON.stringify(prev) !== JSON.stringify(updatedNotifications)
                ? updatedNotifications
                : prev;
        });
    }, []);

    // Memoize fetch function to maintain consistent reference
    const fetchNotifications = useCallback(async () => {
        try {
            const newNotifications = await getNotifications();

            if (newNotifications) {
                // Compare stringified versions of the notifications
                const newNotificationsString = JSON.stringify(newNotifications);

                if (lastFetchRef.current !== newNotificationsString) {
                    lastFetchRef.current = newNotificationsString;
                    setNotifications(newNotifications);
                }
            }
        } catch (error) {
            console.error('Error fetching notifications:', error);
        }

        return;
    }, []);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setIsMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        fetchNotifications(); // Initial fetch

        const intervalId = setInterval(fetchNotifications, 30000);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            clearInterval(intervalId);
        };
    }, [fetchNotifications]);

    // Memoize filtered notifications
    const unreadNotifications = useMemo(() =>
        notifications?.filter(notification => !notification.read) ?? [],
        [notifications]
    );

    // Memoize displayed notifications based on active tab
    const displayedNotifications = useMemo(() =>
        activeTab === 'all' ? notifications ?? [] : unreadNotifications,
        [activeTab, notifications, unreadNotifications]
    );

    const handleTabChange = (tab: 'unread' | 'all') => {
        setActiveTab(tab);
        // Scroll to top when tab changes
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = 0;
        }
    };

    return (
        <div className={`relative ${unreadNotifications.length > 0 ? 'mr-4' : 'mr-2'}`} ref={menuRef}>
            <div
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="cursor-pointer flex items-center justify-center"
            >
                <svg xmlns="http://www.w3.org/2000/svg"
                    className={`h-4 w-4 xl:h-5 xl:w-5 cursor-pointer`}
                    fill={unreadNotifications.length > 0 ? "#FFD700" : "none"}
                    viewBox="0 0 24 24"
                    stroke={unreadNotifications.length > 0 ? "#DAA520" : "currentColor"}>
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                {unreadNotifications.length > 0 && (
                    <div className="absolute -top-2 -right-2 bg-red-500 rounded-full text-white h-4 w-4 sm:h-4 sm:w-4 flex text-xs items-center justify-center cursor-pointer">
                        {unreadNotifications.length}
                    </div>
                )}
            </div>
            {isMenuOpen && (
                <div className="absolute right-0 mt-4 w-[500px] rounded-md shadow-lg bg-white dark:bg-gray-800 dark:text-white ring-1 ring-black ring-opacity-5 z-50">
                    <div className="border-b border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-between px-4 py-2">
                            <div className={`${textColor} text-md xl:text-lg font-bold`}>
                                Notifications
                            </div>
                            <div className="flex space-x-4">
                                <button
                                    onClick={() => handleTabChange('unread')}
                                    className={`font-medium transition-colors ${activeTab === 'unread'
                                        ? 'text-blue-500'
                                        : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                                        }`}
                                >
                                    Unread
                                </button>
                                <button
                                    onClick={() => handleTabChange('all')}
                                    className={`font-medium transition-colors ${activeTab === 'all'
                                        ? 'text-blue-500'
                                        : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                                        }`}
                                >
                                    All
                                </button>
                            </div>
                        </div>
                    </div>
                    <div ref={scrollContainerRef} className="max-h-[min(85vh)] overflow-y-auto">
                        <div className="py-1" role="menu" aria-orientation="vertical">
                            {displayedNotifications.length === 0 ? (
                                <div
                                    className={`w-full text-left px-4 py-4 ${textColor} cursor-pointer`}
                                    role="menuitem"
                                >
                                    {activeTab === 'unread' ? 'No unread notifications.' : 'No notifications.'}
                                </div>
                            ) : (
                                displayedNotifications.map((notification) => (
                                    <div key={notification.guid}
                                        className={`w-full text-left px-4 py-4 ${textColor} hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer relative flex items-start gap-3`}
                                        role="menuitem"
                                        onClick={() => handleReadNotification(notification.guid)}
                                    >
                                        {!notification.read && (
                                            <div className="w-2 h-2 rounded-full bg-blue-500 mt-[6px] flex-shrink-0" />
                                        )}
                                        {notification.read && (
                                            <div className="w-2 h-2 mt-2 flex-shrink-0" />
                                        )}
                                        <div className="flex-1">
                                            <div className={`font-semibold mb-1 ${textSize}`}>{notification.title}</div>
                                            <div className={`text-gray-600 dark:text-gray-400 ${textSize}`}>
                                                {notification.message}
                                            </div>
                                            <div className="flex justify-between items-center mt-2">
                                                <div className={`${textSize} text-gray-600 dark:text-gray-400`}>
                                                    {notification.createdAt.toLocaleString()}
                                                </div>
                                                {notification.read && (
                                                    <button
                                                        onClick={(e) => handleMarkUnread(e, notification.guid)}
                                                        className="text-blue-500 hover:text-blue-600 text-sm"
                                                    >
                                                        Mark as Unread
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default NotificationMenu;